import React from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useQuery } from '@tanstack/react-query';
import { supabase } from "@/integrations/supabase/client";
import { Lead, NewLead } from '@/types/leads';
import { Funnel } from '@/types/funnels';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreHorizontal, Copy, Trash2, Edit, Search } from 'lucide-react';
import { useToast } from "@/hooks/use-toast";
import { format } from 'date-fns';
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";

interface LeadsTableProps {
  leads: Lead[];
  onAddLead: (lead: NewLead) => void;
  onUpdateLead: (id: string, lead: Partial<Lead>) => void;
  onDeleteLead: (id: string) => void;
  filterValue: string;
  onFilterChange: (value: string) => void;
  sortConfig: { key: string; direction: 'ascending' | 'descending' } | null;
  onSort: (config: { key: string; direction: 'ascending' | 'descending' } | null) => void;
  selectedLeads: Lead[];
  onSelectLeads: (leads: Lead[]) => void;
  onExport: () => void;
  onBulkAction: (action: string) => void;
}

const LEADS_PER_PAGE = 8;

const LeadsTable: React.FC<LeadsTableProps> = ({
  leads,
  onAddLead,
  onUpdateLead,
  onDeleteLead,
  filterValue,
  onFilterChange,
  sortConfig,
  onSort,
  selectedLeads,
  onSelectLeads,
  onExport,
  onBulkAction
}) => {
  const { toast } = useToast();
  const [open, setOpen] = React.useState(false);
  const [editOpen, setEditOpen] = React.useState(false);
  const [selectedLead, setSelectedLead] = React.useState<Lead | null>(null);
  const [showLeadDetails, setShowLeadDetails] = React.useState(false);
  const [selectedLeadDetails, setSelectedLeadDetails] = React.useState<Lead | null>(null);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [newLead, setNewLead] = React.useState<NewLead>({
    customer: '',
    last_action: '',
    source: '',
    media_buyer_opinion_notes: '',
    call_file: '',
    spam_rating: '5',
    lead_quality: '',
    sales_person_assigned: '',
    sales_funnel_source: '',
    lead_value: '',
    custom_fields: null,
    tags: [],
    score: null,
    next_follow_up: null,
    status: 'in_progress',
    sales_person_notes: null
  });

  const { data: funnels = [], isLoading: funnelsLoading, error: funnelsError } = useQuery({
    queryKey: ['funnels'],
    queryFn: async () => {
      console.log('Fetching funnels...');
      const { data, error } = await supabase
        .from('funnels')
        .select('*')
        .order('funnel_name', { ascending: true });
      
      if (error) {
        console.error('Error fetching funnels:', error);
        throw error;
      }
      console.log('Funnels fetched:', data);
      return data as Funnel[];
    },
  });

  const handleLeadClick = (lead: Lead) => {
    setSelectedLeadDetails(lead);
    setShowLeadDetails(true);
  };

  const handleEditClick = (e: React.MouseEvent, lead: Lead) => {
    e.stopPropagation();
    setSelectedLead(lead);
    setEditOpen(true);
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedLead) return;

    try {
      await onUpdateLead(selectedLead.id, {
        customer: selectedLead.customer,
        source: selectedLead.source,
        sales_person_assigned: selectedLead.sales_person_assigned,
        lead_value: selectedLead.lead_value,
        last_action: selectedLead.last_action,
        next_follow_up: selectedLead.next_follow_up,
        sales_person_notes: selectedLead.sales_person_notes,
        media_buyer_opinion_notes: selectedLead.media_buyer_opinion_notes,
        spam_rating: selectedLead.spam_rating,
        status: selectedLead.status,
        sales_funnel_source: selectedLead.sales_funnel_source,
        updated_at: new Date().toISOString()
      });

      setEditOpen(false);
      setSelectedLead(null);
      
      toast({
        title: "Success",
        description: "Lead updated successfully",
      });
    } catch (error) {
      console.error('Error updating lead:', error);
      toast({
        title: "Error",
        description: "Failed to update lead. Please try again.",
        variant: "destructive",
      });
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: "Copied to clipboard",
        description: "The lead information has been copied to your clipboard.",
      });
    } catch (err) {
      console.error('Failed to copy text: ', err);
      toast({
        title: "Copy failed",
        description: "Failed to copy lead information to clipboard.",
        variant: "destructive",
      });
    }
  };

  const handleDelete = (id: string) => {
    onDeleteLead(id);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return null;
      }
      return format(date, 'MMM dd, yyyy');
    } catch (error) {
      console.error('Error formatting date:', error);
      return null;
    }
  };

  const filteredLeads = React.useMemo(() => {
    return leads.filter(lead => {
      const searchStr = filterValue.toLowerCase();
      return (
        lead.customer?.toLowerCase().includes(searchStr) ||
        lead.source?.toLowerCase().includes(searchStr) ||
        lead.lead_quality?.toLowerCase().includes(searchStr) ||
        lead.sales_person_assigned?.toLowerCase().includes(searchStr) ||
        lead.sales_funnel_source?.toLowerCase().includes(searchStr)
      );
    });
  }, [leads, filterValue]);

  const paginatedLeads = React.useMemo(() => {
    const startIndex = (currentPage - 1) * LEADS_PER_PAGE;
    return filteredLeads.slice(startIndex, startIndex + LEADS_PER_PAGE);
  }, [filteredLeads, currentPage]);

  const totalPages = Math.ceil(filteredLeads.length / LEADS_PER_PAGE);

  const getLeadQualityFromSpamRating = (spamRating: string | null): string => {
    if (!spamRating) return 'Not Rated';
    const rating = parseInt(spamRating);
    if (rating <= 3) return 'High';
    if (rating <= 6) return 'Medium';
    return 'Low';
  };

  const getQualityBadgeColor = (quality: string): string => {
    switch (quality) {
      case 'High':
        return 'bg-green-500 hover:bg-green-600';
      case 'Medium':
        return 'bg-yellow-500 hover:bg-yellow-600';
      case 'Low':
        return 'bg-red-500 hover:bg-red-600';
      default:
        return 'bg-gray-500 hover:bg-gray-600';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 w-full">
          <div className="relative w-full sm:w-[300px]">
            <Input
              type="text"
              placeholder="Search leads..."
              value={filterValue}
              onChange={(e) => onFilterChange(e.target.value)}
              className="pr-10"
            />
            <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-gray-900 text-white hover:bg-gray-800 w-full sm:w-auto">
                Add Lead
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="text-xl font-bold">Add New Lead 📝</DialogTitle>
                <DialogDescription>
                  Fill in the details below to add a new lead to the system.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={(e) => {
                e.preventDefault();
                onAddLead(newLead);
                setOpen(false);
              }} className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="customer">👤 Customer</Label>
                  <Input
                    id="customer"
                    name="customer"
                    value={newLead.customer}
                    onChange={(e) => setNewLead({ ...newLead, customer: e.target.value })}
                    required
                    placeholder="Customer name"
                    className="w-full"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>🎯 Funnel Source</Label>
                  <Select
                    value={newLead.sales_funnel_source || ''}
                    onValueChange={(value) => setNewLead({ ...newLead, sales_funnel_source: value })}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select funnel source" />
                    </SelectTrigger>
                    <SelectContent>
                      {funnels.map((funnel) => (
                        <SelectItem key={funnel.id} value={funnel.funnel_name}>
                          📊 {funnel.funnel_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="last_action">🔄 Last Action</Label>
                  <Select
                    value={newLead.last_action || ''}
                    onValueChange={(value) => setNewLead({ ...newLead, last_action: value })}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select last action" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="called">📞 Called</SelectItem>
                      <SelectItem value="not_contacted">⏳ Not Contacted</SelectItem>
                      <SelectItem value="emailed">📧 Emailed</SelectItem>
                      <SelectItem value="messaged">💬 Messaged</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="sales_person_notes">📝 Sales Person Notes</Label>
                  <Input
                    id="sales_person_notes"
                    name="sales_person_notes"
                    value={newLead.sales_person_notes || ''}
                    onChange={(e) => setNewLead({ ...newLead, sales_person_notes: e.target.value })}
                    placeholder="Add notes about this lead..."
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="source">📱 Source</Label>
                  <Select
                    value={newLead.source || ''}
                    onValueChange={(value) => setNewLead({ ...newLead, source: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select source" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="meta">📱 META</SelectItem>
                      <SelectItem value="linkedin">💼 LinkedIn</SelectItem>
                      <SelectItem value="referral">👥 Referral</SelectItem>
                      <SelectItem value="other">🔄 Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="media_buyer_opinion_notes">💭 Media Buyer Notes</Label>
                  <Input
                    id="media_buyer_opinion_notes"
                    name="media_buyer_opinion_notes"
                    value={newLead.media_buyer_opinion_notes || ''}
                    onChange={(e) => setNewLead({ ...newLead, media_buyer_opinion_notes: e.target.value })}
                    placeholder="Media buyer's opinion"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="call_file">📞 Call File</Label>
                  <Input
                    id="call_file"
                    name="call_file"
                    type="url"
                    value={newLead.call_file || ''}
                    onChange={(e) => setNewLead({ ...newLead, call_file: e.target.value })}
                    placeholder="Link to call recording"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="spam_rating">🚫 Spam Rating (1-10)</Label>
                  <div className="pt-2">
                    <Slider
                      id="spam_rating"
                      min={1}
                      max={10}
                      step={1}
                      value={[parseInt(newLead.spam_rating || '5')]}
                      onValueChange={(value) => setNewLead({ ...newLead, spam_rating: value[0].toString() })}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>Low Risk</span>
                      <span>High Risk</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="status">📊 Status</Label>
                  <Select
                    value={newLead.status || 'in_progress'}
                    onValueChange={(value: 'won' | 'lost' | 'in_progress') => 
                      setNewLead({ ...newLead, status: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="won">🎉 Won</SelectItem>
                      <SelectItem value="lost">❌ Lost</SelectItem>
                      <SelectItem value="in_progress">⏳ In Progress</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="sales_person_assigned">👨‍💼 Sales Person</Label>
                  <Select
                    value={newLead.sales_person_assigned || ''}
                    onValueChange={(value) => setNewLead({ ...newLead, sales_person_assigned: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Assign to sales person" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Lee">👨‍💼 Lee</SelectItem>
                      <SelectItem value="Zach">👨‍💼 Zach</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="lead_value">💰 Lead Value</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2">$</span>
                    <Input
                      id="lead_value"
                      name="lead_value"
                      type="number"
                      value={newLead.lead_value || ''}
                      onChange={(e) => setNewLead({ ...newLead, lead_value: e.target.value })}
                      className="pl-7"
                      placeholder="0.00"
                      step="0.01"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="next_follow_up">📅 Next Follow Up</Label>
                  <Input
                    id="next_follow_up"
                    name="next_follow_up"
                    type="datetime-local"
                    value={newLead.next_follow_up || ''}
                    onChange={(e) => setNewLead({ ...newLead, next_follow_up: e.target.value })}
                  />
                </div>

                <Button type="submit" className="bg-gray-900 text-white hover:bg-gray-800">
                  Add Lead ✨
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="border rounded-xl overflow-hidden bg-white shadow-lg">
        <div className="overflow-x-auto min-w-full">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#f8f9fa] border-b border-gray-200">
                <TableHead className="w-[40px] py-4">
                  <Checkbox
                    checked={selectedLeads.length === leads.length}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        onSelectLeads(leads);
                      } else {
                        onSelectLeads([]);
                      }
                    }}
                  />
                </TableHead>
                <TableHead className="min-w-[180px] py-4 font-semibold whitespace-nowrap">Customer</TableHead>
                <TableHead className="min-w-[140px] py-4 font-semibold whitespace-nowrap">Source</TableHead>
                <TableHead className="min-w-[140px] py-4 font-semibold whitespace-nowrap">Lead Quality</TableHead>
                <TableHead className="min-w-[160px] text-center py-4 font-semibold whitespace-nowrap">Sales Person</TableHead>
                <TableHead className="min-w-[160px] text-center py-4 font-semibold whitespace-nowrap">Funnel Source</TableHead>
                <TableHead className="min-w-[120px] text-right py-4 font-semibold whitespace-nowrap">Lead Value</TableHead>
                <TableHead className="min-w-[160px] text-center py-4 font-semibold whitespace-nowrap">Last Action</TableHead>
                <TableHead className="min-w-[160px] text-center py-4 font-semibold whitespace-nowrap">Last Updated</TableHead>
                <TableHead className="w-[70px] text-right py-4"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedLeads.map((lead) => (
                <TableRow 
                  key={lead.id}
                  className="cursor-pointer hover:bg-gray-50/50"
                  onClick={() => handleLeadClick(lead)}
                >
                  <TableCell onClick={(e) => e.stopPropagation()} className="w-[40px]">
                    <Checkbox
                      checked={!!selectedLeads.find(selectedLead => selectedLead.id === lead.id)}
                      onCheckedChange={() => {
                        if (selectedLeads.find(selectedLead => selectedLead.id === lead.id)) {
                          onSelectLeads(selectedLeads.filter(selectedLead => selectedLead.id !== lead.id));
                        } else {
                          onSelectLeads([...selectedLeads, lead]);
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell className="min-w-[180px] font-medium">
                    <div className="inline-flex items-center px-3 py-1 rounded-full bg-[#E5DEFF] text-[#6E59A5] whitespace-nowrap">
                      {lead.customer}
                    </div>
                  </TableCell>
                  <TableCell className="min-w-[140px]">
                    <div className="inline-flex items-center px-3 py-1 rounded-full bg-[#D6BCFA] text-[#7E69AB] whitespace-nowrap">
                      {lead.source}
                    </div>
                  </TableCell>
                  <TableCell className="min-w-[140px]">
                    <Badge 
                      variant="default"
                      className={getQualityBadgeColor(getLeadQualityFromSpamRating(lead.spam_rating))}
                    >
                      {getLeadQualityFromSpamRating(lead.spam_rating)}
                    </Badge>
                  </TableCell>
                  <TableCell className="min-w-[160px] text-center">
                    <Select
                      value={lead.sales_person_assigned || ''}
                      onValueChange={(value) => {
                        onUpdateLead(lead.id, { 
                          sales_person_assigned: value,
                          updated_at: new Date().toISOString()
                        });
                      }}
                    >
                      <SelectTrigger className="w-[120px] mx-auto bg-[#F3F4F6]">
                        <SelectValue placeholder="Assign" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Lee">👨‍💼 Lee</SelectItem>
                        <SelectItem value="Zach">👨‍💼 Zach</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="min-w-[160px]">
                    <div className="inline-flex items-center px-3 py-1 rounded-full bg-[#8B5CF6] text-white whitespace-nowrap">
                      {lead.sales_funnel_source}
                    </div>
                  </TableCell>
                  <TableCell className="min-w-[120px] text-right">
                    <div className="inline-flex items-center px-3 py-1 rounded-full bg-[#1A1F2C] text-white whitespace-nowrap">
                      {lead.lead_value ? `$${parseFloat(lead.lead_value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                    </div>
                  </TableCell>
                  <TableCell className="min-w-[160px] text-center">
                    <Select
                      value={lead.last_action || ''}
                      onValueChange={(value) => onUpdateLead(lead.id, { last_action: value })}
                    >
                      <SelectTrigger className="w-[140px] mx-auto bg-[#F3F4F6]">
                        <SelectValue placeholder="Select action" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="called">📞 Called</SelectItem>
                        <SelectItem value="not_contacted">⏳ Not Contacted</SelectItem>
                        <SelectItem value="emailed">📧 Emailed</SelectItem>
                        <SelectItem value="messaged">💬 Messaged</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="min-w-[160px] text-center">
                    <div className="inline-flex items-center px-3 py-1 rounded-full bg-gray-100 text-gray-600 whitespace-nowrap">
                      {formatDate(lead.updated_at || lead.created_at)}
                    </div>
                  </TableCell>
                  <TableCell className="text-right p-2">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" className="h-8 w-8 p-0">
                          <span className="sr-only">Open menu</span>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={(e) => handleEditClick(e, lead)}>
                          <Edit className="mr-2 h-4 w-4" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDelete(lead.id)}>
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 mt-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(page => Math.max(1, page - 1))}
            disabled={currentPage === 1}
          >
            Previous
          </Button>
          <span className="text-sm text-gray-600">
            Page {currentPage} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(page => Math.min(totalPages, page + 1))}
            disabled={currentPage === totalPages}
          >
            Next
          </Button>
        </div>
      )}

      <Dialog open={showLeadDetails} onOpenChange={setShowLeadDetails}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Lead Details</DialogTitle>
          </DialogHeader>
          {selectedLeadDetails && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
              <div className="space-y-4">
                <div>
                  <Label className="font-semibold">Customer</Label>
                  <p className="text-gray-700">{selectedLeadDetails.customer}</p>
                </div>
                <div>
                  <Label className="font-semibold">Source</Label>
                  <p className="text-gray-700">{selectedLeadDetails.source || 'N/A'}</p>
                </div>
                <div>
                  <Label className="font-semibold">Last Action</Label>
                  <p className="text-gray-700">{selectedLeadDetails.last_action || 'No action taken'}</p>
                </div>
                <div>
                  <Label className="font-semibold">Sales Person</Label>
                  <p className="text-gray-700">{selectedLeadDetails.sales_person_assigned || 'Not Assigned'}</p>
                </div>
                <div>
                  <Label className="font-semibold">Funnel Source</Label>
                  <p className="text-gray-700">{selectedLeadDetails.sales_funnel_source || 'N/A'}</p>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <Label className="font-semibold">Lead Value</Label>
                  <p className="text-gray-700">
                    {selectedLeadDetails.lead_value 
                      ? `$${parseFloat(selectedLeadDetails.lead_value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` 
                      : 'N/A'}
                  </p>
                </div>
                <div>
                  <Label className="font-semibold">Status</Label>
                  <Badge variant="outline" className="mt-1">
                    {selectedLeadDetails.status || 'In Progress'}
                  </Badge>
                </div>
                <div>
                  <Label className="font-semibold">Created At</Label>
                  <p className="text-gray-700">{formatDate(selectedLeadDetails.created_at)}</p>
                </div>
                <div>
                  <Label className="font-semibold">Last Updated</Label>
                  <p className="text-gray-700">{formatDate(selectedLeadDetails.updated_at)}</p>
                </div>
                <div>
                  <Label className="font-semibold">Next Follow Up</Label>
                  <p className="text-gray-700">{formatDate(selectedLeadDetails.next_follow_up) || 'Not scheduled'}</p>
                </div>
                <div>
                  <Label className="font-semibold">Lead Quality</Label>
                  <Badge 
                    variant="default"
                    className={getQualityBadgeColor(getLeadQualityFromSpamRating(selectedLeadDetails.spam_rating))}
                  >
                    {getLeadQualityFromSpamRating(selectedLeadDetails.spam_rating)}
                  </Badge>
                </div>
                <div>
                  <Label className="font-semibold">Spam Rating</Label>
                  <p className="text-gray-700">{selectedLeadDetails.spam_rating || 'Not rated'}</p>
                </div>
              </div>
              <div className="col-span-2 space-y-4">
                <div>
                  <Label className="font-semibold">Sales Person Notes</Label>
                  <p className="text-gray-700 whitespace-pre-wrap">{selectedLeadDetails.sales_person_notes || 'No notes available'}</p>
                </div>
                <div>
                  <Label className="font-semibold">Media Buyer Notes</Label>
                  <p className="text-gray-700 whitespace-pre-wrap">{selectedLeadDetails.media_buyer_opinion_notes || 'No notes available'}</p>
                </div>
                {selectedLeadDetails.call_file && (
                  <div>
                    <Label className="font-semibold">Call Recording</Label>
                    <Button 
                      className="mt-2 inline-flex items-center gap-2"
                      onClick={() => window.open(selectedLeadDetails.call_file, '_blank')}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 8L17 12L15 8L14 12L12 8L11 12L9 8L8 12L6 8L5 12L3 8" />
                        <circle cx="12" cy="4" r="2" />
                      </svg>
                      Listen to Call
                    </Button>
                  </div>
                )}
                <Button
                  onClick={(e) => {
                    setShowLeadDetails(false);
                    handleEditClick(e, selectedLeadDetails);
                  }}
                  className="mt-4"
                >
                  Edit Lead
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Lead</DialogTitle>
          </DialogHeader>
          {selectedLead && (
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-customer">Customer</Label>
                  <Input
                    id="edit-customer"
                    name="customer"
                    value={selectedLead.customer}
                    onChange={(e) => setSelectedLead({ ...selectedLead, customer: e.target.value })}
                    required
                    placeholder="Customer name"
                    className="w-full"
                  />
                </div>
                <div className="space-y-2">
                  <Label>🎯 Funnel Source</Label>
                  <Select
                    value={selectedLead.sales_funnel_source || ''}
                    onValueChange={(value) => setSelectedLead({ ...selectedLead, sales_funnel_source: value })}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select funnel source" />
                    </SelectTrigger>
                    <SelectContent>
                      {funnels.map((funnel) => (
                        <SelectItem key={funnel.id} value={funnel.funnel_name}>
                          📊 {funnel.funnel_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="last_action">🔄 Last Action</Label>
                  <Select
                    value={selectedLead.last_action || ''}
                    onValueChange={(value) => setSelectedLead({ ...selectedLead, last_action: value })}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select last action" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="called">📞 Called</SelectItem>
                      <SelectItem value="not_contacted">⏳ Not Contacted</SelectItem>
                      <SelectItem value="emailed">📧 Emailed</SelectItem>
                      <SelectItem value="messaged">💬 Messaged</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="sales_person_notes">📝 Sales Person Notes</Label>
                  <Input
                    id="sales_person_notes"
                    name="sales_person_notes"
                    value={selectedLead.sales_person_notes || ''}
                    onChange={(e) => setSelectedLead({ ...selectedLead, sales_person_notes: e.target.value })}
                    placeholder="Add notes about this lead..."
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="source">📱 Source</Label>
                  <Select
                    value={selectedLead.source || ''}
                    onValueChange={(value) => setSelectedLead({ ...selectedLead, source: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select source" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="meta">📱 META</SelectItem>
                      <SelectItem value="linkedin">💼 LinkedIn</SelectItem>
                      <SelectItem value="referral">👥 Referral</SelectItem>
                      <SelectItem value="other">🔄 Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="media_buyer_opinion_notes">💭 Media Buyer Notes</Label>
                  <Input
                    id="media_buyer_opinion_notes"
                    name="media_buyer_opinion_notes"
                    value={selectedLead.media_buyer_opinion_notes || ''}
                    onChange={(e) => setSelectedLead({ ...selectedLead, media_buyer_opinion_notes: e.target.value })}
                    placeholder="Media buyer's opinion"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="call_file">📞 Call File</Label>
                  <Input
                    id="call_file"
                    name="call_file"
                    type="url"
                    value={selectedLead.call_file || ''}
                    onChange={(e) => setSelectedLead({ ...selectedLead, call_file: e.target.value })}
                    placeholder="Link to call recording"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="spam_rating">🚫 Spam Rating (1-10)</Label>
                  <div className="pt-2">
                    <Slider
                      id="spam_rating"
                      min={1}
                      max={10}
                      step={1}
                      value={[parseInt(selectedLead.spam_rating || '5')]}
                      onValueChange={(value) => setSelectedLead({ ...selectedLead, spam_rating: value[0].toString() })}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>Low Risk</span>
                      <span>High Risk</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="status">📊 Status</Label>
                  <Select
                    value={selectedLead.status || 'in_progress'}
                    onValueChange={(value: 'won' | 'lost' | 'in_progress') => 
                      setSelectedLead({ ...selectedLead, status: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="won">🎉 Won</SelectItem>
                      <SelectItem value="lost">❌ Lost</SelectItem>
                      <SelectItem value="in_progress">⏳ In Progress</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="sales_person_assigned">👨‍💼 Sales Person</Label>
                  <Select
                    value={selectedLead.sales_person_assigned || ''}
                    onValueChange={(value) => setSelectedLead({ ...selectedLead, sales_person_assigned: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Assign to sales person" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Lee">👨‍💼 Lee</SelectItem>
                      <SelectItem value="Zach">👨‍💼 Zach</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="lead_value">💰 Lead Value</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2">$</span>
                    <Input
                      id="lead_value"
                      name="lead_value"
                      type="number"
                      value={selectedLead.lead_value || ''}
                      onChange={(e) => setSelectedLead({ ...selectedLead, lead_value: e.target.value })}
                      className="pl-7"
                      placeholder="0.00"
                      step="0.01"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="next_follow_up">📅 Next Follow Up</Label>
                  <Input
                    id="next_follow_up"
                    name="next_follow_up"
                    type="datetime-local"
                    value={selectedLead.next_follow_up || ''}
                    onChange={(e) => setSelectedLead({ ...selectedLead, next_follow_up: e.target.value })}
                  />
                </div>

                <Button type="submit" className="bg-gray-900 text-white hover:bg-gray-800">
                  Save Changes
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeadsTable;
